
import { useMemo } from 'react';
import { useProjects } from '@/hooks/useProjects';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useWasteFactors } from '@/hooks/useWasteFactors';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { SELECT_ESTIMATE_FOR_DASHBOARD } from '@/lib/supabaseSelects';
import { isStratigraphyPriceStale } from '@/utils/estimates/priceStaleCheck';
import { DashboardFilters, estimateMatchesFilters } from '@/contexts/DashboardFiltersContext';

const EMPTY_FILTERS: DashboardFilters = {
  customerIds: [],
  statuses: [],
  stratigraphyName: null,
  supplier: null,
};

/**
 * Periodi disponibili per il filtro dashboard. Filtrano le metriche di
 * "flusso" (preventivi creati nel periodo, valore vinto nel periodo, ecc.).
 * Le metriche di "stato corrente" (progetti attivi, fornitori in anagrafica)
 * non sono filtrate dal periodo.
 * 'custom' usa il parametro customRange { from, to } passato a useDashboardData.
 */
export type DashboardPeriod = '1m' | '3m' | '6m' | '12m' | 'ytd' | 'all' | 'custom';

export interface DashboardCustomRange {
  from: Date | null;
  to: Date | null;
}

export interface DashboardMetrics {
  // Project metrics (stato corrente, non filtrato)
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  archivedProjects: number;

  // Estimate metrics (filtrate da periodo)
  totalEstimates: number;
  draftEstimates: number;
  pendingEstimates: number;    // F30: include sent + legacy pending
  approvedEstimates: number;   // legacy
  contractedEstimates: number; // F30: include won + legacy contracted
  /** F30: preventivi marcati come Persi nel periodo. */
  lostEstimates: number;

  // Financial metrics
  totalValue: number;
  contractedValue: number;     // vinti + legacy contracted
  pendingValue: number;        // inviati ancora aperti = pipeline aperta
  /** F30: valore preventivi persi (per analisi "quanto valeva quello che ci è sfuggito"). */
  lostValue: number;
  averageEstimateValue: number;
  /** Conversion rate vinti / (vinti + persi) — escludi i preventivi ancora in pipeline. */
  conversionRate: number;
  /** F30: giorni medi tra sent_at e won_at/lost_at sui preventivi chiusi nel periodo. */
  avgDaysToClose: number;
  /** Aggregazione motivi perdita F30. */
  lostReasonsBreakdown: Array<{ reason: string; count: number; value: number }>;

  // Materials metrics
  totalMaterialsCost: number;
  totalLaborCost: number;
  totalSurfaceArea: number;
  averageCostPerSqm: number;

  // Operational metrics
  wastePercentage: number;
  disposalPercentage: number;
  avgInstallationTime: number;

  // Supplier breakdown
  supplierBreakdown: Array<{
    supplier: string;
    totalCost: number;
    percentage: number;
  }>;

  // Material category breakdown
  categoryBreakdown: Array<{
    category: string;
    totalCost: number;
    percentage: number;
    items: number;
  }>;

  // Monthly trends (ultimi 12 mesi indipendentemente dal filtro periodo)
  monthlyTrends: Array<{
    month: string;
    /** ISO della prima ora del mese (per drill-time da click). */
    monthStartIso: string;
    /** ISO della fine giornata dell'ultimo giorno del mese. */
    monthEndIso: string;
    projects: number;
    estimates: number;
    /** Valore totale preventivi creati nel mese (somma totalAmount). */
    value: number;
    /** F30: valore preventivi *vinti* nel mese (somma vinti). */
    wonValue: number;
    area: number;
  }>;

  /** F16: top 5 stratigrafie più usate (per numero di volte aggiunte ai preventivi). */
  topStratigraphies: Array<{
    name: string;
    usedCount: number;
    totalArea: number;
    totalValue: number;
  }>;

  /**
   * F16: alert preventivi "stale" — inviati da più di N giorni senza chiusura.
   * Default soglia 30 giorni (recuperabile in futuro da org_settings).
   */
  staleEstimates: Array<{
    id: string;
    name: string;
    daysSinceSent: number;
    totalAmount: number;
  }>;

  /**
   * Preventivi con prezzi disallineati: almeno un material referenziato nelle
   * loro stratigrafie è stato aggiornato DOPO l'ultimo bulk update prezzi della
   * riga. Significa che la riga preventivo mostra un prezzo non più allineato
   * al catalogo.
   * Vengono inclusi solo i preventivi NON locked (won/lost/contracted): per
   * quelli locked il prezzo è cristallizzato di proposito.
   */
  pricesStaleEstimates: Array<{
    id: string;
    name: string;
    status: string;
    totalAmount: number;
    /** Numero di stratigrafie con almeno un material aggiornato dopo prices_updated_at. */
    staleRows: number;
  }>;

  /**
   * Offerte in scadenza: preventivi sent/pending con offer_issued_at + validity_days
   * vicino al "today" o già passato. Solo NON locked.
   * daysToExpiry: 0 = scade oggi, negativo = già scaduta da X giorni.
   */
  expiringEstimates: Array<{
    id: string;
    name: string;
    status: string;
    totalAmount: number;
    daysToExpiry: number;
    expired: boolean;
  }>;
}

/**
 * Confronto vs periodo precedente: KPI essenziali computati sulla finestra
 * temporale immediatamente precedente a quella corrente. Servono per mostrare
 * "+X% vs precedente" sulle hero card.
 */
export interface PreviousPeriodComparison {
  wonValue: number;
  wonCount: number;
  lostValue: number;
  lostCount: number;
  pendingValue: number;
  totalEstimates: number;
  averageEstimateValue: number;
  conversionRate: number;
  avgDaysToClose: number;
  /** Numero di giorni della finestra (per labeling). */
  windowDays: number;
}

/**
 * Converte un periodo nella data di cutoff. `null` = nessun filtro.
 */
function periodToCutoff(period: DashboardPeriod): Date | null {
  const now = new Date();
  if (period === 'all') return null;
  if (period === 'ytd') {
    return new Date(now.getFullYear(), 0, 1);
  }
  const monthMap: Record<string, number> = { '1m': 1, '3m': 3, '6m': 6, '12m': 12 };
  const months = monthMap[period];
  if (!months) return null;
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - months);
  return cutoff;
}

const LOST_REASON_LABELS: Record<string, string> = {
  price: 'Prezzo troppo alto',
  timing: 'Tempi non compatibili',
  competitor: 'Concorrente scelto',
  no_response: 'Nessuna risposta',
  other: 'Altro motivo',
};

export const useDashboardData = (
  period: DashboardPeriod = '6m',
  filters: DashboardFilters = EMPTY_FILTERS,
  customRange?: DashboardCustomRange,
) => {
  const { wasteMap } = useWasteFactors();
  const { user } = useAuth();
  const { projects, isLoading: projectsLoading } = useProjects();
  const { currentOrganizationId } = useCurrentOrganization();

  // F34/soglia stale: leggi stale_days_threshold dall'org (default 30 lato DB)
  // + validity_days dalle settings (per "offerte in scadenza")
  const { data: orgConfig = { staleDays: 30, validityDays: 30 } } = useQuery({
    queryKey: ['org-dashboard-config', currentOrganizationId],
    queryFn: async (): Promise<{ staleDays: number; validityDays: number }> => {
      if (!currentOrganizationId) return { staleDays: 30, validityDays: 30 };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('organizations')
        .select('stale_days_threshold, settings')
        .eq('id', currentOrganizationId)
        .maybeSingle();
      if (error || !data) return { staleDays: 30, validityDays: 30 };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const settings = (data.settings as any) ?? {};
      return {
        staleDays: Number(data.stale_days_threshold ?? 30),
        validityDays: Number(settings.offer_validity_days ?? 30),
      };
    },
    enabled: !!currentOrganizationId,
    staleTime: 5 * 60_000,
  });
  const staleDaysThreshold = orgConfig.staleDays;
  const validityDays = orgConfig.validityDays;

  // Fetch comprehensive estimate data.
  // BUG-FIX (post F16): qui prima rifacevamo `projects WHERE user_id = user.id`
  // bypassando la logica org-based di useProjects. Se l'utente era in
  // un'organizzazione, i progetti veri (filtrati per organization_id) NON
  // venivano trovati → estimatesData vuoto → dashboard piatta.
  // Ora prendiamo i project IDs direttamente da `projects` (caricato sopra
  // con la logica corretta). Niente seconda query DB e niente discrepanze.
  const projectIdsKey = projects.map(p => p.id).sort().join(',');
  const { data: estimatesData = [], isLoading: estimatesLoading } = useQuery({
    queryKey: ['dashboard-estimates-comprehensive', user?.id, projectIdsKey],
    queryFn: async () => {
      if (!user || projects.length === 0) return [];
      const projectIds = projects.map(p => p.id);

      // PostgREST embed: shape canonico in `lib/supabaseSelects` per evitare:
      //  - PGRST201 (FK ambigua) → embed esplicito `projects!estimates_project_id_fkey`
      //  - F31 bug (campi materiali mancanti) → shape centralizzato
      const { data, error } = await supabase
        .from('estimates')
        .select(SELECT_ESTIMATE_FOR_DASHBOARD)
        .in('project_id', projectIds);

      if (error) {
        console.error('Error fetching comprehensive estimates:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!user && projects.length > 0,
  });

  // F24: customers per top-clienti
  const { data: customersData = [] } = useQuery({
    queryKey: ['dashboard-customers', user?.id],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await supabase
        .from('customers' as any)
        .select('id, name, city');
      if (error) {
        console.warn('[dashboard] Failed to fetch customers:', error);
        return [];
      }
      return data || [];
    },
    enabled: !!user,
  });

  // Fetch materials data for analysis
  // Paginazione client-side per superare PostgREST max-rows=1000 (Supabase default).
  // Post-import wholesale Knauf+SG+Fassa il DB ha 2735+ materiali e .range() singolo
  // non basta — il server tronca a 1000 indipendentemente dal range richiesto.
  const { data: materialsData = [], isLoading: materialsLoading } = useQuery({
    queryKey: ['dashboard-materials', user?.id],
    queryFn: async () => {
      const CHUNK = 1000;
      const allRows: unknown[] = [];
      let from = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data, error } = await supabase
          // Prezzi NETTI dell'org corrente: la tabella `materials` grezza ha
          // un `unit_price` denormalizzato che il trigger di ricalcolo scrive
          // con lo sconto di UNA organizzazione (vedi useEstimateStratigraphiesQuery),
          // quindi il breakdown costi per categoria/fornitore usava prezzi altrui.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from('materials_with_pricing' as any)
          .select('*')
          .range(from, from + CHUNK - 1);
        if (error) throw error;
        const chunk = (data ?? []) as unknown[];
        allRows.push(...chunk);
        if (chunk.length < CHUNK) break;
        from += CHUNK;
        if (from >= 20000) break;
      }
      return allRows;
    },
  });

  const dashboardMetrics = useMemo((): DashboardMetrics => {
    const cutoff = periodToCutoff(period);
    // Per range custom serve sia "from" (cutoff) sia "to" (upper bound).
    const customFrom = period === 'custom' && customRange?.from ? customRange.from : null;
    const customTo = period === 'custom' && customRange?.to ? customRange.to : null;
    const inPeriod = (d: string | null | undefined): boolean => {
      if (period === 'custom') {
        if (!d) return false;
        const dt = new Date(d);
        if (customFrom && dt < customFrom) return false;
        if (customTo) {
          // Includi tutto il giorno "to" (fino 23:59:59.999)
          const toEnd = new Date(customTo);
          toEnd.setHours(23, 59, 59, 999);
          if (dt > toEnd) return false;
        }
        return true;
      }
      if (!cutoff) return true;
      if (!d) return false;
      return new Date(d) >= cutoff;
    };

    // ============== PROGETTI (stato corrente, non filtrato) ==============
    const activeProjects = projects.filter(p => p.status === 'active').length;
    const completedProjects = projects.filter(p => p.status === 'completed').length;
    const archivedProjects = projects.filter(p => p.status === 'archived').length;

    // ============== PREVENTIVI (filtrati per periodo + cross-filters utente) ==============
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filteredEstimates = (estimatesData as any[])
      .filter(e => inPeriod(e.created_at))
      .filter(e => estimateMatchesFilters(e, filters));

    const draftEstimates = filteredEstimates.filter(e => e.status === 'draft').length;
    const pendingEstimates = filteredEstimates.filter(
      e => e.status === 'sent' || e.status === 'pending',
    ).length;
    const approvedEstimates = filteredEstimates.filter(e => e.status === 'approved').length;
    const contractedEstimates = filteredEstimates.filter(
      e => e.status === 'won' || e.status === 'contracted',
    ).length;
    const lostEstimates = filteredEstimates.filter(e => e.status === 'lost').length;

    // ============== METRICHE FINANZIARIE ==============
    const num = (v: unknown): number => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const totalValue = filteredEstimates.reduce((sum, e) => sum + num(e.total_amount), 0);
    const contractedValue = filteredEstimates
      .filter(e => e.status === 'won' || e.status === 'contracted')
      .reduce((sum, e) => sum + num(e.total_amount), 0);
    const pendingValue = filteredEstimates
      .filter(e => e.status === 'sent' || e.status === 'pending' || e.status === 'approved')
      .reduce((sum, e) => sum + num(e.total_amount), 0);
    const lostValue = filteredEstimates
      .filter(e => e.status === 'lost')
      .reduce((sum, e) => sum + num(e.total_amount), 0);

    // Conversion rate: solo sui preventivi *chiusi* (vinti + persi).
    // Esclude la pipeline ancora aperta che non ha esito definito.
    const closedCount = contractedEstimates + lostEstimates;
    const conversionRate = closedCount > 0
      ? (contractedEstimates / closedCount) * 100
      : 0;

    // ============== F30: TEMPO MEDIO CHIUSURA ==============
    // Per i preventivi chiusi (won/lost) nel periodo, calcoliamo giorni tra
    // sent_at e won_at/lost_at. Se sent_at è NULL (transizione diretta
    // draft→won/lost senza passare da sent) usiamo created_at.
    const closedEstimates = filteredEstimates.filter(
      e => e.status === 'won' || e.status === 'lost' || e.status === 'contracted',
    );
    let totalClosingDays = 0;
    let closingCount = 0;
    for (const e of closedEstimates) {
      const start = e.sent_at ? new Date(e.sent_at) : new Date(e.created_at);
      const end = e.won_at
        ? new Date(e.won_at)
        : e.lost_at
        ? new Date(e.lost_at)
        : null;
      if (!end) continue;
      const days = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
      if (days >= 0) {
        totalClosingDays += days;
        closingCount++;
      }
    }
    const avgDaysToClose = closingCount > 0 ? totalClosingDays / closingCount : 0;

    // ============== F30: MOTIVI PERDITA ==============
    const lostReasonsMap = new Map<string, { count: number; value: number }>();
    for (const e of filteredEstimates.filter(x => x.status === 'lost')) {
      const reason = (e.lost_reason as string) || 'other';
      const existing = lostReasonsMap.get(reason) || { count: 0, value: 0 };
      lostReasonsMap.set(reason, {
        count: existing.count + 1,
        value: existing.value + num(e.total_amount),
      });
    }
    const lostReasonsBreakdown = Array.from(lostReasonsMap.entries())
      .map(([reason, data]) => ({
        reason: LOST_REASON_LABELS[reason] || reason,
        count: data.count,
        value: data.value,
      }))
      .sort((a, b) => b.count - a.count);

    // ============== MATERIALI / OPERATIVI ==============
    let totalMaterialsCost = 0;
    let totalLaborCost = 0;
    let totalSurfaceArea = 0;
    let totalWastePercentage = 0;
    let totalDisposalPercentage = 0;
    let totalInstallationTime = 0;
    let stratigraphyCount = 0;

    const supplierCosts = new Map<string, number>();
    const categoryCosts = new Map<string, { cost: number; items: number }>();
    // F16: aggregazione stratigrafie per nome (top usate)
    const stratigraphyUseMap = new Map<string, { count: number; area: number; value: number }>();

    filteredEstimates.forEach(estimate => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (estimate.estimate_stratigraphies as any[] | null)?.forEach((strati) => {
        totalSurfaceArea += num(strati.area);
        stratigraphyCount++;

        // Top stratigrafie per uso
        const stratName = strati.name || 'Senza nome';
        const ex = stratigraphyUseMap.get(stratName) ?? { count: 0, area: 0, value: 0 };
        stratigraphyUseMap.set(stratName, {
          count: ex.count + 1,
          area: ex.area + num(strati.area),
          value: ex.value + num(strati.total_cost),
        });

        if (strati.stratigraphy_data && typeof strati.stratigraphy_data === 'object' && 'layers' in strati.stratigraphy_data) {
          const stratigraphyDataObj = strati.stratigraphy_data as { layers?: unknown[] };
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          stratigraphyDataObj.layers?.forEach((layer: any) => {
            if (layer.materials) {
              const material = layer.materials;
              const layerCost = num(material.unit_price) * num(strati.area) * num(material.incidence_per_sqm || 1);

              totalMaterialsCost += layerCost;

              const supplier = material.supplier || 'Non specificato';
              supplierCosts.set(supplier, (supplierCosts.get(supplier) || 0) + layerCost);

              const category = material.category || 'Altri';
              const current = categoryCosts.get(category) || { cost: 0, items: 0 };
              categoryCosts.set(category, {
                cost: current.cost + layerCost,
                items: current.items + 1,
              });

              const wasteOverride = material.waste_percentage;
              const resolvedWaste = (wasteOverride !== null && wasteOverride !== undefined)
                ? Number(wasteOverride)
                : (wasteMap[material.category ?? ''] ?? 0);
              totalWastePercentage += resolvedWaste;
              totalDisposalPercentage += material.disposal_percentage || 4;
              totalInstallationTime += material.installation_time_per_sqm || 0;
            }

            if (layer.labor_cost_per_sqm) {
              totalLaborCost += layer.labor_cost_per_sqm * num(strati.area);
            }
          });
        }
      });
    });

    // Supplier breakdown
    const totalSupplierCost = Array.from(supplierCosts.values()).reduce((sum, c) => sum + c, 0);
    const supplierBreakdown = Array.from(supplierCosts.entries())
      .map(([supplier, cost]) => ({
        supplier,
        totalCost: cost,
        percentage: totalSupplierCost > 0 ? (cost / totalSupplierCost) * 100 : 0,
      }))
      .sort((a, b) => b.totalCost - a.totalCost);

    // Category breakdown
    const totalCategoryCost = Array.from(categoryCosts.values()).reduce((sum, item) => sum + item.cost, 0);
    const categoryBreakdown = Array.from(categoryCosts.entries())
      .map(([category, data]) => ({
        category,
        totalCost: data.cost,
        percentage: totalCategoryCost > 0 ? (data.cost / totalCategoryCost) * 100 : 0,
        items: data.items,
      }))
      .sort((a, b) => b.totalCost - a.totalCost);

    // Top stratigrafie (top 5 per count)
    const topStratigraphies = Array.from(stratigraphyUseMap.entries())
      .map(([name, data]) => ({
        name,
        usedCount: data.count,
        totalArea: data.area,
        totalValue: data.value,
      }))
      .sort((a, b) => b.usedCount - a.usedCount)
      .slice(0, 5);

    // ============== MONTHLY TRENDS (ultimi 12 mesi, NON filtrati da periodo) ==============
    // I trends mostrano sempre l'andamento storico, indipendentemente dal
    // filtro periodo. Servono a vedere stagionalità.
    const monthsLabels = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
    const monthlyTrends: DashboardMetrics['monthlyTrends'] = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1, 0, 0, 0, 0);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59, 999);

      const monthProjects = projects.filter(p => {
        const c = new Date(p.createdAt);
        return c >= monthStart && c <= monthEnd;
      }).length;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const monthEstimates = (estimatesData as any[]).filter(e => {
        const c = new Date(e.created_at);
        return c >= monthStart && c <= monthEnd;
      });

      const value = monthEstimates.reduce((s, e) => s + num(e.total_amount), 0);
      const area = monthEstimates.reduce((s, e) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return s + ((e.estimate_stratigraphies as any[] | null)?.reduce((a, x) => a + num(x.area), 0) || 0);
      }, 0);
      // wonValue = somma totalAmount dei preventivi *vinti* in quel mese (basato su won_at o created_at)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const wonInMonth = (estimatesData as any[]).filter(e => {
        if (e.status !== 'won' && e.status !== 'contracted') return false;
        const winDate = e.won_at ? new Date(e.won_at) : new Date(e.updated_at || e.created_at);
        return winDate >= monthStart && winDate <= monthEnd;
      });
      const wonValue = wonInMonth.reduce((s, e) => s + num(e.total_amount), 0);

      monthlyTrends.push({
        month: monthsLabels[monthStart.getMonth()],
        monthStartIso: monthStart.toISOString(),
        monthEndIso: monthEnd.toISOString(),
        projects: monthProjects,
        estimates: monthEstimates.length,
        value,
        wonValue,
        area,
      });
    }

    // ============== F16: STALE ESTIMATES ALERT ==============
    // Preventivi inviati da > N giorni senza chiusura (rimasti in stato sent/pending).
    // Soglia configurabile per org (stale_days_threshold), default 30.
    const STALE_THRESHOLD_DAYS = staleDaysThreshold;
    const nowMs = Date.now();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const staleEstimates = (estimatesData as any[])
      .filter(e => e.status === 'sent' || e.status === 'pending')
      .map(e => {
        const sentDate = e.sent_at ? new Date(e.sent_at) : new Date(e.updated_at || e.created_at);
        const daysSinceSent = (nowMs - sentDate.getTime()) / (1000 * 60 * 60 * 24);
        return {
          id: e.id as string,
          name: (e.name as string) || 'Preventivo',
          daysSinceSent,
          totalAmount: num(e.total_amount),
        };
      })
      .filter(e => e.daysSinceSent >= STALE_THRESHOLD_DAYS)
      .sort((a, b) => b.daysSinceSent - a.daysSinceSent)
      .slice(0, 10);

    // ============== PRICES STALE: preventivi con prezzi disallineati ==============
    // Confronto: material.updated_at > strat.prices_updated_at significa che il
    // catalogo è cambiato dopo l'ultimo "Aggiorna prezzi" su quella riga.
    // Skip preventivi locked (won/lost/contracted) — i loro prezzi sono congelati di proposito.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const materialUpdateMap = new Map<string, number>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (materialsData as any[]).forEach(m => {
      if (m?.id && m?.updated_at) {
        materialUpdateMap.set(m.id, new Date(m.updated_at).getTime());
      }
    });

    // ============== OFFERTE IN SCADENZA ==============
    // Calcolo: offer_issued_at + validityDays = scadenza. Solo preventivi
    // sent/pending non locked. expired = già scaduta. Inclusi anche quelli
    // che scadono entro i prossimi 5 giorni come warning.
    const expiringEstimates: DashboardMetrics['expiringEstimates'] = [];
    const todayMs = Date.now();
    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    const EXPIRING_SOON_DAYS = 5;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const e of (estimatesData as any[])) {
      if (e.status !== 'sent' && e.status !== 'pending') continue;
      const issuedAt = e.offer_issued_at ?? e.sent_at;
      if (!issuedAt) continue;
      const expiry = new Date(issuedAt).getTime() + validityDays * MS_PER_DAY;
      const daysToExpiry = Math.floor((expiry - todayMs) / MS_PER_DAY);
      if (daysToExpiry > EXPIRING_SOON_DAYS) continue;
      expiringEstimates.push({
        id: e.id,
        name: e.name || 'Preventivo',
        status: e.status,
        totalAmount: num(e.total_amount),
        daysToExpiry,
        expired: daysToExpiry < 0,
      });
    }
    expiringEstimates.sort((a, b) => a.daysToExpiry - b.daysToExpiry);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pricesStaleEstimates: DashboardMetrics['pricesStaleEstimates'] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const e of (estimatesData as any[])) {
      // Skip locked: prezzi congelati apposta
      if (e.status === 'won' || e.status === 'lost' || e.status === 'contracted') continue;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const stratis = (e.estimate_stratigraphies as any[] | null) ?? [];
      if (stratis.length === 0) continue;
      let staleRows = 0;
      for (const s of stratis) {
        if (isStratigraphyPriceStale(s, materialUpdateMap)) staleRows++;
      }
      if (staleRows > 0) {
        pricesStaleEstimates.push({
          id: e.id,
          name: e.name || 'Preventivo',
          status: e.status,
          totalAmount: num(e.total_amount),
          staleRows,
        });
      }
    }

    return {
      totalProjects: projects.length,
      activeProjects,
      completedProjects,
      archivedProjects,

      totalEstimates: filteredEstimates.length,
      draftEstimates,
      pendingEstimates,
      approvedEstimates,
      contractedEstimates,
      lostEstimates,

      totalValue,
      contractedValue,
      pendingValue,
      lostValue,
      averageEstimateValue: filteredEstimates.length > 0 ? totalValue / filteredEstimates.length : 0,
      conversionRate,
      avgDaysToClose,
      lostReasonsBreakdown,

      totalMaterialsCost,
      totalLaborCost,
      totalSurfaceArea,
      averageCostPerSqm: totalSurfaceArea > 0 ? (totalMaterialsCost + totalLaborCost) / totalSurfaceArea : 0,

      wastePercentage: stratigraphyCount > 0 ? totalWastePercentage / stratigraphyCount : 0,
      disposalPercentage: stratigraphyCount > 0 ? totalDisposalPercentage / stratigraphyCount : 0,
      avgInstallationTime: stratigraphyCount > 0 ? totalInstallationTime / stratigraphyCount : 0,

      supplierBreakdown,
      categoryBreakdown,
      monthlyTrends,

      topStratigraphies,
      staleEstimates,
      pricesStaleEstimates,
      expiringEstimates,
    };
  }, [projects, estimatesData, materialsData, wasteMap, period, staleDaysThreshold, validityDays, filters, customRange?.from, customRange?.to]);

  // F16: top customers — calcolo separato per non gonfiare il useMemo principale
  const topCustomers = useMemo(() => {
    const cutoff = periodToCutoff(period);
    const customFrom = period === 'custom' && customRange?.from ? customRange.from : null;
    const customTo = period === 'custom' && customRange?.to ? customRange.to : null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const map = new Map<string, { name: string; city: string | null; totalWon: number; count: number }>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const e of (estimatesData as any[])) {
      const created = new Date(e.created_at);
      if (period === 'custom') {
        if (customFrom && created < customFrom) continue;
        if (customTo) {
          const toEnd = new Date(customTo);
          toEnd.setHours(23, 59, 59, 999);
          if (created > toEnd) continue;
        }
      } else if (cutoff && created < cutoff) {
        continue;
      }
      // Cross-filters: rispetta status/customer/stratigrafia/supplier selezionati.
      // Per "top clienti per fatturato vinto" continuiamo a richiedere status won.
      if (!estimateMatchesFilters(e, filters)) continue;
      const isWon = e.status === 'won' || e.status === 'contracted';
      if (!isWon) continue;
      const customerId: string | null = e.projects?.customer_id ?? null;
      if (!customerId) continue;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cust = (customersData as any[]).find(c => c.id === customerId);
      if (!cust) continue;
      const ex = map.get(customerId) ?? { name: cust.name, city: cust.city, totalWon: 0, count: 0 };
      ex.totalWon += Number(e.total_amount ?? 0);
      ex.count += 1;
      map.set(customerId, ex);
    }
    return Array.from(map.entries())
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.totalWon - a.totalWon)
      .slice(0, 5);
  }, [estimatesData, customersData, period, filters, customRange?.from, customRange?.to]);

  // ============== TOP CLIENTI PER WIN-RATE (F16 — affidabilità) ==============
  // Quali clienti chiudono di più: vinti / (vinti + persi). Min 3 chiusi per
  // essere statisticamente sensato. Ordina per win-rate desc + valore vinto.
  const topCustomersByWinRate = useMemo(() => {
    const cutoff = periodToCutoff(period);
    const customFrom = period === 'custom' && customRange?.from ? customRange.from : null;
    const customTo = period === 'custom' && customRange?.to ? customRange.to : null;
    const map = new Map<string, {
      name: string;
      city: string | null;
      wonCount: number;
      lostCount: number;
      wonValue: number;
      lostValue: number;
    }>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const e of (estimatesData as any[])) {
      const created = new Date(e.created_at);
      if (period === 'custom') {
        if (customFrom && created < customFrom) continue;
        if (customTo) {
          const toEnd = new Date(customTo);
          toEnd.setHours(23, 59, 59, 999);
          if (created > toEnd) continue;
        }
      } else if (cutoff && created < cutoff) {
        continue;
      }
      if (!estimateMatchesFilters(e, filters)) continue;
      const customerId: string | null = e.projects?.customer_id ?? null;
      if (!customerId) continue;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cust = (customersData as any[]).find(c => c.id === customerId);
      if (!cust) continue;
      const isWon = e.status === 'won' || e.status === 'contracted';
      const isLost = e.status === 'lost';
      if (!isWon && !isLost) continue;
      const ex = map.get(customerId) ?? {
        name: cust.name, city: cust.city,
        wonCount: 0, lostCount: 0, wonValue: 0, lostValue: 0,
      };
      if (isWon) {
        ex.wonCount++;
        ex.wonValue += Number(e.total_amount ?? 0);
      } else {
        ex.lostCount++;
        ex.lostValue += Number(e.total_amount ?? 0);
      }
      map.set(customerId, ex);
    }
    return Array.from(map.entries())
      .map(([id, d]) => {
        const total = d.wonCount + d.lostCount;
        return {
          id,
          name: d.name,
          city: d.city,
          wonCount: d.wonCount,
          lostCount: d.lostCount,
          wonValue: d.wonValue,
          totalClosed: total,
          winRate: total > 0 ? (d.wonCount / total) * 100 : 0,
        };
      })
      .filter(c => c.totalClosed >= 3)  // soglia minima per statistical significance
      .sort((a, b) => b.winRate - a.winRate || b.wonValue - a.wonValue)
      .slice(0, 5);
  }, [estimatesData, customersData, period, filters, customRange?.from, customRange?.to]);

  // ============== PREVIOUS PERIOD COMPARISON ==============
  // Stessa metrica calcolata sulla finestra immediatamente precedente, alla
  // stessa larghezza. 'all' non ha un "prima" sensato → null.
  const previousPeriodComparison = useMemo<PreviousPeriodComparison | null>(() => {
    // Calcola la finestra "current" (start, end). Per range custom usa from/to.
    // Per period preset: start = cutoff, end = now.
    const now = new Date();
    let curStart: Date | null = null;
    let curEnd: Date = now;
    if (period === 'custom') {
      if (!customRange?.from) return null;
      curStart = customRange.from;
      if (customRange.to) {
        const t = new Date(customRange.to);
        t.setHours(23, 59, 59, 999);
        curEnd = t;
      }
    } else if (period === 'all') {
      return null;
    } else {
      curStart = periodToCutoff(period);
      if (!curStart) return null;
    }

    const windowMs = curEnd.getTime() - curStart.getTime();
    if (windowMs <= 0) return null;
    const prevEnd = new Date(curStart.getTime() - 1);
    const prevStart = new Date(curStart.getTime() - windowMs);

    const num = (v: unknown): number => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const prevEstimates = (estimatesData as any[]).filter(e => {
      const created = new Date(e.created_at);
      if (created < prevStart || created > prevEnd) return false;
      return estimateMatchesFilters(e, filters);
    });

    const wonCount = prevEstimates.filter(e => e.status === 'won' || e.status === 'contracted').length;
    const lostCount = prevEstimates.filter(e => e.status === 'lost').length;
    const wonValue = prevEstimates
      .filter(e => e.status === 'won' || e.status === 'contracted')
      .reduce((s, e) => s + num(e.total_amount), 0);
    const lostValue = prevEstimates
      .filter(e => e.status === 'lost')
      .reduce((s, e) => s + num(e.total_amount), 0);
    const pendingValue = prevEstimates
      .filter(e => e.status === 'sent' || e.status === 'pending')
      .reduce((s, e) => s + num(e.total_amount), 0);
    const totalEstimates = prevEstimates.length;
    const totalValue = prevEstimates.reduce((s, e) => s + num(e.total_amount), 0);
    const averageEstimateValue = totalEstimates > 0 ? totalValue / totalEstimates : 0;
    const closed = wonCount + lostCount;
    const conversionRate = closed > 0 ? (wonCount / closed) * 100 : 0;

    // Tempo medio chiusura: replica logica del main useMemo
    let totalClosingDays = 0;
    let closingCount = 0;
    for (const e of prevEstimates) {
      if (e.status !== 'won' && e.status !== 'lost' && e.status !== 'contracted') continue;
      const start = e.sent_at ? new Date(e.sent_at) : new Date(e.created_at);
      const end = e.won_at ? new Date(e.won_at) : e.lost_at ? new Date(e.lost_at) : null;
      if (!end) continue;
      const days = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
      if (days >= 0) { totalClosingDays += days; closingCount++; }
    }
    const avgDaysToClose = closingCount > 0 ? totalClosingDays / closingCount : 0;

    return {
      wonValue, wonCount,
      lostValue, lostCount,
      pendingValue,
      totalEstimates,
      averageEstimateValue,
      conversionRate,
      avgDaysToClose,
      windowDays: Math.round(windowMs / (1000 * 60 * 60 * 24)),
    };
  }, [estimatesData, period, customRange?.from, customRange?.to, filters]);

  return {
    dashboardMetrics,
    topCustomers,
    topCustomersByWinRate,
    previousPeriodComparison,
    /** Anagrafica clienti completa — per popolare il multi-select del filtro. */
    customersData,
    isLoading: projectsLoading || estimatesLoading || materialsLoading,
    projects,
    estimatesData,
    materialsData,
    /** Soglia "stale" letta da organizations.stale_days_threshold (default 30). */
    staleDaysThreshold,
  };
};
